import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Download } from "lucide-react";
import { getEmergencyInfo } from "../../data/emergencyData";

/* ── Printable document ──────────────────────────────────────────────────────── */
const PrintableDoc = React.forwardRef(({ trip }, ref) => {
  const result = trip?.result || {};
  const { itineraire = [], resume, point_fort, incontournables = [], budget_estime, infos_pratiques } = result;

  // Emergency info: merge Claude data + static fallback
  const emergencyInfo = getEmergencyInfo(trip?.destination || "", result?.urgences);

  const styles = {
    page: { fontFamily: "Georgia, 'Times New Roman', serif", background: "#F9F6F0", color: "#2B2623", padding: "40px 48px", maxWidth: "800px", margin: "0 auto" },
    header: { borderBottom: "2px solid #C17F59", paddingBottom: "24px", marginBottom: "32px" },
    logo: { fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "1.1rem", color: "#C17F59", marginBottom: "12px", letterSpacing: "0.02em" },
    title: { fontFamily: "Georgia, serif", fontSize: "2.8rem", fontWeight: 600, color: "#2B2623", margin: "0 0 4px 0", lineHeight: 1.1 },
    subtitle: { fontFamily: "Georgia, serif", fontSize: "1rem", color: "#9A8F86", fontStyle: "italic", margin: 0 },
    section: { marginBottom: "28px" },
    sectionTitle: { fontFamily: "Georgia, serif", fontSize: "1.4rem", fontWeight: 600, color: "#2B2623", borderBottom: "1px solid #DCD4C6", paddingBottom: "6px", marginBottom: "14px" },
    quote: { fontFamily: "Georgia, serif", fontStyle: "italic", color: "#5C554E", fontSize: "0.95rem", lineHeight: 1.7, background: "#FDF8F3", borderLeft: "3px solid #C17F59", padding: "12px 16px", margin: "0 0 16px 0" },
    dayBlock: { marginBottom: "20px", pageBreakInside: "avoid" },
    dayHeader: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" },
    dayNum: { width: "32px", height: "32px", borderRadius: "50%", background: "#C17F59", color: "#F9F6F0", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", fontWeight: 600, fontSize: "0.85rem", flexShrink: 0 },
    dayTitle: { fontFamily: "Georgia, serif", fontSize: "1.15rem", fontWeight: 600, color: "#2B2623", margin: 0 },
    dayJour: { fontFamily: "Georgia, serif", fontSize: "0.75rem", color: "#8B5E34", textTransform: "uppercase", letterSpacing: "0.08em" },
    dayDesc: { fontFamily: "Georgia, serif", fontSize: "0.88rem", color: "#5C554E", lineHeight: 1.75, marginLeft: "44px" },
    tags: { display: "flex", gap: "8px", flexWrap: "wrap", marginLeft: "44px", marginTop: "6px" },
    tag: { fontFamily: "Georgia, serif", fontSize: "0.75rem", color: "#8B5E34", border: "1px solid #DCD4C6", padding: "2px 10px", background: "#FDF8F3" },
    budgetGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" },
    budgetItem: { background: "#F1EBE1", border: "1px solid #DCD4C6", padding: "10px 14px" },
    budgetLabel: { fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#8B5E34", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "2px" },
    budgetValue: { fontFamily: "Georgia, serif", fontSize: "1.1rem", fontWeight: 600, color: "#2B2623" },
    budgetTotal: { background: "#C17F59", color: "#F9F6F0", padding: "14px 18px", gridColumn: "1 / -1", marginTop: "4px" },
    urgenceGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" },
    urgenceItem: { background: "#fff5f5", border: "1px solid #fecaca", padding: "10px 14px" },
    urgenceLabel: { fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.08em" },
    urgenceValue: { fontFamily: "Georgia, serif", fontSize: "0.88rem", fontWeight: 600, color: "#2B2623", marginTop: "2px" },
    infoRow: { display: "flex", gap: "12px", padding: "7px 0", borderBottom: "1px solid #DCD4C6" },
    infoLabel: { fontFamily: "Georgia, serif", fontSize: "0.75rem", color: "#8B5E34", textTransform: "uppercase", letterSpacing: "0.08em", minWidth: "140px", paddingTop: "2px" },
    infoValue: { fontFamily: "Georgia, serif", fontSize: "0.88rem", color: "#5C554E", lineHeight: 1.6 },
    footer: { marginTop: "48px", borderTop: "1px solid #DCD4C6", paddingTop: "16px", textAlign: "center" },
    footerText: { fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "0.8rem", color: "#9A8F86" },
    pointFort: { background: "#FDF8F3", borderLeft: "3px solid #C17F59", padding: "12px 16px", marginBottom: "16px" },
    pointFortLabel: { fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#C17F59", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" },
    pointFortText: { fontFamily: "Georgia, serif", fontSize: "0.9rem", color: "#5C554E", fontStyle: "italic" },
    inco: { fontFamily: "Georgia, serif", fontSize: "0.88rem", color: "#5C554E", padding: "4px 0", display: "flex", alignItems: "flex-start", gap: "8px" },
  };

  const infoRows = infos_pratiques ? [
    { label: "Visa", value: infos_pratiques.visa },
    { label: "Passeport", value: infos_pratiques.passeport },
    { label: "Santé & vaccins", value: infos_pratiques.sante_vaccins },
    { label: "Monnaie", value: infos_pratiques.monnaie_info },
    { label: "Décalage horaire", value: infos_pratiques.decalage_horaire },
    { label: "Électricité", value: infos_pratiques.electricite },
    { label: "Langue", value: infos_pratiques.langue_principale },
    { label: "Transport local", value: infos_pratiques.transport_local_detail },
  ].filter(r => r.value) : [];

  const urgenceItems = [
    { label: "Police", value: emergencyInfo.police },
    { label: "Pompiers", value: emergencyInfo.pompiers },
    { label: "SAMU / Ambulance", value: emergencyInfo.samu },
    { label: "Urgences universelles", value: emergencyInfo.urgences_universelles },
    ...(emergencyInfo.hopital ? [{ label: "Hôpital de référence", value: emergencyInfo.hopital }] : []),
  ].filter((u) => u.value);

  const budgetItems = budget_estime ? [
    { label: "Vols A/R", value: budget_estime.vols_aller_retour },
    { label: "Hébergement", value: budget_estime.hebergement_total },
    { label: "Activités", value: budget_estime.activites_total },
    { label: "Restauration", value: budget_estime.repas_total },
    { label: "Transport local", value: budget_estime.transport_local },
    { label: "Divers (10%)", value: budget_estime.divers },
  ].filter(b => b.value) : [];

  return (
    <div ref={ref} style={styles.page}>
      {/* ── Cover ── */}
      <div style={styles.header}>
        <p style={styles.logo}>Voyage-r</p>
        <h1 style={styles.title}>{trip?.destination}</h1>
        <p style={styles.subtitle}>
          {trip?.date_depart && new Date(trip.date_depart + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          {" — "}
          {trip?.date_retour && new Date(trip.date_retour + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          {trip?.nb_adultes && ` · ${trip.nb_adultes} voyageur${trip.nb_adultes > 1 ? "s" : ""}`}
          {trip?.nb_enfants > 0 && `, ${trip.nb_enfants} enfant${trip.nb_enfants > 1 ? "s" : ""}`}
        </p>
      </div>

      {/* ── Résumé ── */}
      {resume && <p style={styles.quote}>{resume}</p>}

      {/* ── Point fort ── */}
      {point_fort && (
        <div style={styles.pointFort}>
          <p style={styles.pointFortLabel}>À ne pas manquer</p>
          <p style={styles.pointFortText}>{point_fort}</p>
        </div>
      )}

      {/* ── Incontournables ── */}
      {incontournables.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Incontournables</h2>
          {incontournables.map((item, i) => (
            <div key={i} style={styles.inco}>
              <span style={{ color: "#C17F59", fontWeight: 600 }}>›</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Itinéraire ── */}
      {itineraire.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Itinéraire — {itineraire.length} jours</h2>
          {itineraire.map((day, i) => (
            <div key={i} style={styles.dayBlock}>
              <div style={styles.dayHeader}>
                <div style={{ ...styles.dayNum, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
                <div>
                  <p style={styles.dayJour}>{day.jour}</p>
                  <p style={styles.dayTitle}>{day.titre}</p>
                </div>
              </div>
              <p style={styles.dayDesc}>{day.description}</p>
              {(day.repas_suggeres || day.hebergement_suggere || day.budget_jour) && (
                <div style={styles.tags}>
                  {day.repas_suggeres && <span style={styles.tag}>🍽 {day.repas_suggeres}</span>}
                  {day.hebergement_suggere && <span style={{ ...styles.tag, background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534" }}>🏨 {day.hebergement_suggere}</span>}
                  {day.budget_jour && <span style={styles.tag}>{day.budget_jour}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Budget ── */}
      {budgetItems.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Budget estimé</h2>
          <div style={styles.budgetGrid}>
            {budgetItems.map((b, i) => (
              <div key={i} style={styles.budgetItem}>
                <p style={styles.budgetLabel}>{b.label}</p>
                <p style={styles.budgetValue}>{b.value}</p>
              </div>
            ))}
            {budget_estime?.total && (
              <div style={styles.budgetTotal}>
                <p style={{ ...styles.budgetLabel, color: "#F9F6F0", opacity: 0.8 }}>Total estimé</p>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "1.8rem", fontWeight: 600, color: "#F9F6F0", margin: "4px 0 0 0" }}>{budget_estime.total}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Infos pratiques ── */}
      {infoRows.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations pratiques</h2>
          {infoRows.map((r, i) => (
            <div key={i} style={styles.infoRow}>
              <span style={styles.infoLabel}>{r.label}</span>
              <span style={styles.infoValue}>{r.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Footer ── */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          Voyage-r · Itinéraire préparé sur mesure par votre conseiller voyage · © {new Date().getFullYear()}
        </p>
        <p style={{ ...styles.footerText, marginTop: "4px" }}>
          Document généré le {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {/* ── Urgences & Ambassade — à la fin du document ── */}
      <div style={{ ...styles.section, marginTop: "32px", pageBreakBefore: "always" }}>
        <h2 style={{ ...styles.sectionTitle, color: "#C17F59", borderBottomColor: "#DEC4B8" }}>
          Informations utiles & Urgences
        </h2>

        {/* Diego's intro */}
        <p style={{ ...styles.quote, borderColor: "#C17F59", marginBottom: "20px", color: "#5C554E" }}>
          Je vous souhaite un voyage sans encombre, mais parce qu'on ne sait jamais : voici les contacts essentiels sur place en cas d'urgence.
        </p>

        {/* Emergency numbers */}
        {urgenceItems.length > 0 && (
          <>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#8B5E34", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "8px" }}>
              Numéros d'urgence locaux
            </p>
            <div style={styles.urgenceGrid}>
              {urgenceItems.map((u, i) => (
                <div key={i} style={styles.urgenceItem}>
                  <p style={styles.urgenceLabel}>{u.label}</p>
                  <p style={styles.urgenceValue}>{u.value}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* French Embassy */}
        {emergencyInfo.ambassade && (
          <div style={{ marginTop: "16px", background: "#FDF6F3", border: "1px solid #DEC4B8", padding: "14px 16px" }}>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#C17F59", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>
              🇫🇷 Ambassade de France
            </p>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "0.95rem", fontWeight: 600, color: "#2B2623", margin: "0 0 4px 0" }}>
              {emergencyInfo.ambassade.nom}
            </p>
            {emergencyInfo.ambassade.adresse && (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "0.82rem", color: "#5C554E", margin: "0 0 3px 0" }}>
                📍 {emergencyInfo.ambassade.adresse}
              </p>
            )}
            {(emergencyInfo.ambassade.telephone || emergencyInfo.ambassade.telephone_urgence) && (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "0.88rem", fontWeight: 600, color: "#2B2623", margin: "0 0 3px 0" }}>
                ☎ {emergencyInfo.ambassade.telephone || emergencyInfo.ambassade.telephone_urgence}
              </p>
            )}
            {emergencyInfo.ambassade.site && (
              <p style={{ fontFamily: "Georgia, serif", fontSize: "0.78rem", color: "#C17F59", margin: 0 }}>
                {emergencyInfo.ambassade.site}
              </p>
            )}
          </div>
        )}

        <p style={{ fontFamily: "Georgia, serif", fontSize: "0.72rem", color: "#9A8F86", fontStyle: "italic", marginTop: "12px" }}>
          Numéros vérifiés à la date de création de votre dossier. En cas de doute, consultez diplomatie.gouv.fr
        </p>
      </div>
    </div>
  );
});
PrintableDoc.displayName = "PrintableDoc";

/* ── Export Button ───────────────────────────────────────────────────────────── */
export function PDFExportButton({ trip }) {
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Voyage-r — ${trip?.destination || "Itinéraire"}`,
    pageStyle: `
      @page { size: A4; margin: 10mm 12mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .no-print { display: none !important; }
      }
    `,
  });

  return (
    <>
      <button
        onClick={handlePrint}
        className="inline-flex items-center gap-2 px-4 py-2 border border-[#DCD4C6] text-[#5C554E] hover:border-[#C17F59] hover:text-[#C17F59] transition-colors text-sm"
        style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "1px" }}
        data-testid="pdf-export-btn"
      >
        <Download size={13} strokeWidth={1.5} />
        Télécharger mon carnet de route
      </button>
      {/* Hidden printable document */}
      <div style={{ display: "none" }}>
        <PrintableDoc ref={printRef} trip={trip} />
      </div>
    </>
  );
}
